import { JubliaAIRedirectEnum } from '@base/enums/jublia-ai.enum';
import { MenuItemsId } from '@base/enums/menu-config.enum';
import { MenuItem } from '@base/models/others/menu';

/**
 * Defines the configuration for the navigation bar menu items.
 * Each menu item can have nested children and various display options.
 * @param eventDir - target event directory
 * @param isBCP - to support for custom cmhk2026
 */
export const navbarMenu = (eventDir: string, isBCP = false): MenuItem[] => {
  if (eventDir) {
    return [
      {
        id: MenuItemsId.EXPLORE,
        name: 'EXPLORE',
        navigate: [eventDir, 'explore'],
        icon: 'ri-sparkling-2-line',
        isShow: true,
        isMobile: true,
        children: [],
      },
      {
        id: MenuItemsId.AGENDA,
        name: 'AGENDA',
        navigate: [eventDir, 'agenda', 'sessions'],
        icon: 'ri-time-line',
        isShow: true,
        isMobile: true,
        children: [
          {
            id: MenuItemsId.AGENDA_SEARCH,
            name: 'SESSIONS',
            navigate: [eventDir, 'agenda', 'sessions'],
            isShow: true,
            children: [],
          },
          {
            id: MenuItemsId.AGENDA_ON_DEMAND,
            name: 'ON_DEMAND',
            navigate: [eventDir, 'agenda', 'on-demand'],
            isShow: true,
            children: [],
          },
          {
            id: MenuItemsId.AGENDA_SPEAKERS,
            name: 'SPEAKERS',
            navigate: [eventDir, 'agenda', 'speaker'],
            isShow: true,
            children: [],
          },
          {
            id: MenuItemsId.AGENDA_TOP_PICKS,
            name: 'TOP_PICKS',
            navigate: [eventDir, 'agenda', 'fy'],
            isShow: true,
            icon: 'ri-sparkling-2-line',
            children: [],
          },
        ],
      },
      {
        id: MenuItemsId.MEET,
        name: 'MEET',
        navigate: [eventDir, 'meet', 's', 'people'],
        icon: 'ri-shake-hands-line',
        isShow: true,
        isMobile: true,
        children: [
          {
            id: MenuItemsId.MEET_PEOPLE,
            name: 'PEOPLE',
            navigate: [eventDir, 'meet', 's', 'people'],
            isShow: true,
            children: [],
          },
          {
            id: MenuItemsId.MEET_COMPANY,
            name: 'COMPANIES',
            navigate: [eventDir, 'meet', 's', 'companies'],
            isShow: true,
            children: [],
          },
          {
            id: MenuItemsId.MEET_PRODUCT,
            name: 'PRODUCTS',
            navigate: [eventDir, 'meet', 's', 'products'],
            isShow: true,
            children: [],
          },
          {
            id: MenuItemsId.MEET_REQUEST_RECEIVED,
            name: 'REQUESTS_RECEIVED',
            navigate: [eventDir, 'meet', 'received'],
            isShow: true,
            children: [],
          },
          {
            id: MenuItemsId.MEET_REQUEST_SENT,
            name: 'REQUESTS_SENT',
            navigate: [eventDir, 'meet', 'sent'],
            isShow: true,
            children: [],
          },
          // THIS MENU ONLY SHOWS FOR CUSTOM BOOKMARKED_YOU PAGE
          {
            id: MenuItemsId.BOOKMARKED_YOU,
            name: 'BOOKMARKED_YOU',
            navigate: [eventDir, 'meet', 'bookmarked-you'],
            isShow: isBCP,
            children: [],
          },
          // THIS MENU ONLY SHOWS FOR CUSTOM BOOKMARKED_YOU PAGE
          {
            id: MenuItemsId.MEET_TOP_PICKS,
            name: 'TOP_PICKS',
            navigate: [eventDir, 'meet', 'fy', 'people'],
            isShow: true,
            icon: 'ri-sparkling-2-line',
            children: [],
          },
        ],
      },
      {
        id: MenuItemsId.PLANNER,
        name: 'PLANNER',
        navigate: [eventDir, 'planner', 'me'],
        icon: 'ri-calendar-event-line',
        isMobile: true,
        isShow: true,

        children: [
          {
            id: MenuItemsId.MY_SCHEDULE,
            name: 'MY_SCHEDULE',
            navigate: [eventDir, 'planner', 'me'],
            isShow: true,
            children: [],
          },
          {
            id: MenuItemsId.BOOKMARK,
            name: 'BOOKMARK',
            navigate: [eventDir, 'planner', 'bookmark'],
            isShow: true,
            children: [],
          },
          {
            id: MenuItemsId.TIME_AVAILABILITY,
            name: 'TIME_AVAILABILITY',
            navigate: [eventDir, 'planner', 'time'],
            isShow: true,
            children: [],
          },
          {
            id: MenuItemsId.PLANNER_FLOORPLAN,
            name: 'FLOORPLAN',
            navigate: [eventDir, 'planner', 'floorplan'],
            isShow: false,
            children: [],
          },
        ],
      },
    ];
  }

  return defaultMenu;
};

export const defaultMenu = [
  {
    id: MenuItemsId.EXPLORE,
    name: 'EXPLORE',
    navigate: [''],
    icon: 'ri-sparkling-2-line',
    isShow: true,
    isMobile: true,
    children: [],
  },
];

export const JubliaAIMenu = (eventDir: string): MenuItem[] => {
  if (eventDir) {
    return [
      {
        id: MenuItemsId.EXPLORE,
        name: 'ALL',
        navigate: [eventDir, 'explore'],
        icon: 'ri-sparkling-2-line',
        isShow: true,
        isMobile: true,
        children: [],
      },
      {
        id: MenuItemsId.AGENDA,
        name: 'AGENDA',
        navigate: [eventDir, 'agenda', 'sessions'],
        icon: 'ri-time-line',
        isShow: true,
        isMobile: true,
        children: [],
        target: JubliaAIRedirectEnum.AGENDA,
      },
      {
        id: MenuItemsId.MEET_PEOPLE,
        name: 'PEOPLE',
        navigate: [eventDir, 'meet', 's', 'people'],
        isShow: true,
        children: [],
        target: JubliaAIRedirectEnum.PEOPLE,
      },
      {
        id: MenuItemsId.MEET_COMPANY,
        name: 'COMPANIES',
        navigate: [eventDir, 'meet', 's', 'companies'],
        isShow: true,
        children: [],
        target: JubliaAIRedirectEnum.COMPANY,
      },
      {
        id: MenuItemsId.MEET_PRODUCT,
        name: 'PRODUCTS',
        navigate: [eventDir, 'meet', 's', 'products'],
        isShow: true,
        children: [],
        target: JubliaAIRedirectEnum.PRODUCT,
      },
      {
        id: MenuItemsId.AGENDA_SPEAKERS,
        name: 'SPEAKER',
        navigate: [eventDir, 'agenda', 'speaker'],
        isShow: false,
        children: [],
        target: JubliaAIRedirectEnum.SPEAKER,
      },
    ];
  }

  return defaultMenu;
};

export const JubliaAIRedirection = (eventDir: string, target: JubliaAIRedirectEnum): string[] => {
  switch (target) {
    case JubliaAIRedirectEnum.PEOPLE:
      return [eventDir, 'meet', 's', 'people'];
    case JubliaAIRedirectEnum.COMPANY:
      return [eventDir, 'meet', 's', 'companies'];
    case JubliaAIRedirectEnum.PRODUCT:
      return [eventDir, 'meet', 's', 'products'];
    case JubliaAIRedirectEnum.AGENDA:
      return [eventDir, 'agenda', 'sessions'];
    case JubliaAIRedirectEnum.SPEAKER:
      return [eventDir, 'agenda', 'speaker'];
    case JubliaAIRedirectEnum.PLANNER:
      return [eventDir, 'planner', 'me'];
    case JubliaAIRedirectEnum.TIME_AVAILABILITY:
      return [eventDir, 'planner', 'time'];
    case JubliaAIRedirectEnum.SETTINGS:
      return [eventDir, 'menu', 'settings'];
    case JubliaAIRedirectEnum.EDIT_PROFILE:
      return [eventDir, 'menu', 'edit', 'me'];
    case JubliaAIRedirectEnum.EDIT_COMPANY:
      return [eventDir, 'menu', 'edit', 'company'];
    case JubliaAIRedirectEnum.ANALYTICS:
      return [eventDir, 'menu', 'performance'];
    case JubliaAIRedirectEnum.REQUEST_RECEIVED:
      return [eventDir, 'meet', 'received'];
    case JubliaAIRedirectEnum.BOOKMARKED_PEOPLE:
      return [eventDir, 'planner', 'bookmark'];
    case JubliaAIRedirectEnum.BOOKMARKED_COMPANIES:
      return [eventDir, 'planner', 'bookmark'];
    case JubliaAIRedirectEnum.BOOKMARKED_PRODUCTS:
      return [eventDir, 'planner', 'bookmark'];
    case JubliaAIRedirectEnum.BOOKMARKED_AGENDA:
      return [eventDir, 'planner', 'me'];
    case JubliaAIRedirectEnum.ARCHIVED:
      return [eventDir, 'meet', 'archived'];
    case JubliaAIRedirectEnum.REQUEST_SENT:
      return [eventDir, 'meet', 'sent'];
    case JubliaAIRedirectEnum.RECOMENDATIONS:
    case JubliaAIRedirectEnum.NOTIFICATIONS:
      return [eventDir];
    default:
      return [];
  }
};
